import {
  Box,
  Button,
  Center,
  Link as ChakraLink,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Progress,
  Text,
  VStack,
} from '@chakra-ui/react';
import { getCallUsData, hyphenatePhoneNumber } from '@constants/phoneNumbers';
import { Routes } from '@constants/routes';
import { useSem } from '@contexts/SemContextProvider';
import wordmark from '@public/assets/logo/wordmarks/fsm-wordmark-black.svg';
import { isValidUSPhoneNumber } from '@utils/validatePhone';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { usePostHog } from 'posthog-js/react';
import React, { useEffect, useState } from 'react';
import { FaCommentDots, FaPhoneAlt } from 'react-icons/fa';

// Simplified Navbar component
const SimplifiedNavbar = () => {
  const { dynamicPhoneNumber } = useSem();
  const callUsData = getCallUsData(dynamicPhoneNumber);

  return (
    <Flex
      as="nav"
      align="center"
      justify="space-between"
      wrap="wrap"
      paddingY={4}
      paddingX={[4, 8]}
      bg="white"
      borderBottom="10px solid"
      borderColor="blue.500"
    >
      <Flex align="center" mr={5} flexShrink={0}>
        <Link href={Routes.index}>
          <Box display="flex" width={[136, 230]} height={['25px', '40px']} position="relative">
            <Image src={wordmark} alt="Fair Square Medicare Wordmark" fill priority />
          </Box>
        </Link>
      </Flex>

      <Flex align="center">
        <Text fontWeight="bold" fontSize={['md', 'lg']}>
          <a href={callUsData.href} style={{ textDecoration: 'none', color: 'inherit', paddingLeft: '4px' }}>
            {hyphenatePhoneNumber(dynamicPhoneNumber)}
          </a>
        </Text>
      </Flex>
    </Flex>
  );
};

export default function MultiStepQuizPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 11;
  const { dynamicPhoneNumber } = useSem();
  const posthog = usePostHog();

  // Quiz answers state
  const [hasMedicare, setHasMedicare] = useState<string | undefined>(undefined);
  const [needsDrugCoverage, setNeedsDrugCoverage] = useState<string | undefined>(undefined);
  const [wantsHearingBenefits, setWantsHearingBenefits] = useState<string | undefined>(undefined);
  const [wantsVisionBenefits, setWantsVisionBenefits] = useState<string | undefined>(undefined);
  const [wantsDentalBenefits, setWantsDentalBenefits] = useState<string | undefined>(undefined);
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [firstName, setFirstName] = useState('');

  const [formattedPhoneNumber, setFormattedPhoneNumber] = useState('');
  const [rawPhoneNumber, setRawPhoneNumber] = useState('');
  const [contactPreference, setContactPreference] = useState<string | undefined>(undefined);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const router = useRouter();

  const proceedToNextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      // On the last step (success page), the main button itself handles navigation or there is no button
      // For this quiz, we might want to redirect to home or a specific thank you page.
      // For now, let's assume the success page (step 11) doesn't auto-redirect via proceedToNextStep.
    }
  };

  const validateAndProceed = () => {
    if (currentStep === 10) {
      // Corresponds to the new phone number input step
      if (!isValidUSPhoneNumber(rawPhoneNumber)) {
        setPhoneError('Please enter a valid US phone number.');
        return false;
      }
      setPhoneError(null);
    }
    // Add other validations for DOB, ZIP if needed, though basic check is on button disable
    if (currentStep === 6 && !dateOfBirth.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      // Optionally set an error state for DOB
      return false;
    }
    if (currentStep === 7 && !zipCode.match(/^\d{5}(-\d{4})?$/)) {
      // Optionally set an error state for ZIP
      return false;
    }
    if (currentStep === 8 && firstName.trim() === '') {
      // Optionally set an error state for First Name
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (validateAndProceed()) {
      proceedToNextStep();
    }
  };

  const handleDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^\d]/g, ''); // Remove non-digits

    if (value.length > 8) {
      value = value.slice(0, 8); // Limit to 8 digits (MMDDYYYY)
    }

    if (value.length >= 2 && value.length < 4) {
      value = `${value.slice(0, 2)}/${value.slice(2)}`;
    } else if (value.length >= 4) {
      value = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4)}`;
    }
    setDateOfBirth(value);
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const currentRawValue = e.target.value.replace(/[^\d]/g, ''); // Keep only digits
    setRawPhoneNumber(currentRawValue);

    let formatted = '';
    const startsWithOne = currentRawValue.startsWith('1');
    // Use a temporary variable for the digits to be formatted (stripping leading '1' if present for main formatting logic)
    let digitsToFormat = startsWithOne ? currentRawValue.substring(1) : currentRawValue;

    if (startsWithOne) {
      if (digitsToFormat.length === 0) {
        formatted = '1';
      } else if (digitsToFormat.length <= 3) {
        formatted = `1 (${digitsToFormat}`;
      } else if (digitsToFormat.length <= 6) {
        formatted = `1 (${digitsToFormat.slice(0, 3)}) ${digitsToFormat.slice(3)}`;
      } else if (digitsToFormat.length <= 10) {
        formatted = `1 (${digitsToFormat.slice(0, 3)}) ${digitsToFormat.slice(3, 6)}-${digitsToFormat.slice(6, 10)}`;
      } else {
        // Limit length
        formatted = `1 (${digitsToFormat.slice(0, 3)}) ${digitsToFormat.slice(3, 6)}-${digitsToFormat.slice(6, 10)}`;
        // Update rawPhoneNumber as well if we are truncating the input for formatting
        setRawPhoneNumber(currentRawValue.slice(0, 11));
      }
    } else {
      // Does not start with 1
      if (digitsToFormat.length <= 3) {
        // For initial typing of area code, allow incomplete parenthesis
        formatted = digitsToFormat.length > 0 ? `(${digitsToFormat}` : '';
      } else if (digitsToFormat.length <= 6) {
        formatted = `(${digitsToFormat.slice(0, 3)}) ${digitsToFormat.slice(3)}`;
      } else if (digitsToFormat.length <= 10) {
        formatted = `(${digitsToFormat.slice(0, 3)}) ${digitsToFormat.slice(3, 6)}-${digitsToFormat.slice(6, 10)}`;
      } else {
        // Limit length for non-1 prefixed numbers
        formatted = `(${digitsToFormat.slice(0, 3)}) ${digitsToFormat.slice(3, 6)}-${digitsToFormat.slice(6, 10)}`;
        // Update rawPhoneNumber as well if we are truncating the input for formatting
        setRawPhoneNumber(currentRawValue.slice(0, 10));
      }
    }
    setFormattedPhoneNumber(formatted);
  };

  const submitDataAndRedirect = React.useCallback(async () => {
    if (hasSubmitted) return;

    setIsSubmitting(true);
    const formData = {
      hasMedicare,
      needsDrugCoverage,
      wantsHearingBenefits,
      wantsVisionBenefits,
      wantsDentalBenefits,
      dateOfBirth,
      zipCode,
      firstName,
      phoneNumber: rawPhoneNumber,
      contactPreference: contactPreference,
      source: 'Quiz Form',
    };

    try {
      const response = await fetch('/api/notify-slack-contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        console.error('Slack notification failed (auto-submit):', await response.text());
      }
    } catch (error) {
      console.error('Error during automatic Slack notification submission:', error);
    }
    setHasSubmitted(true);
    setIsSubmitting(false);
  }, [
    hasMedicare,
    needsDrugCoverage,
    wantsHearingBenefits,
    wantsVisionBenefits,
    wantsDentalBenefits,
    dateOfBirth,
    zipCode,
    firstName,
    rawPhoneNumber,
    contactPreference,
    hasSubmitted,
    setIsSubmitting,
    setHasSubmitted,
  ]);

  useEffect(() => {
    if (currentStep === totalSteps - 1 && contactPreference && rawPhoneNumber && !hasSubmitted && !isSubmitting) {
      // Auto-submit when on phone entry step (10) and about to go to success (11)
      // This logic might need adjustment based on whether we want auto-submit before success page
      // For now, let's keep the manual submit on the phone page button
    }
  }, [currentStep, totalSteps, contactPreference, rawPhoneNumber, hasSubmitted, isSubmitting, submitDataAndRedirect]);

  const handleMainButtonAction = async () => {
    if (currentStep < totalSteps - 1) {
      // For steps before phone input and success page
      if (validateAndProceed()) {
        // PostHog for general next button clicks on input steps (6,7,8)
        if ([6, 7, 8].includes(currentStep)) {
          posthog?.capture('quiz_form_step_proceed', {
            form_type: 'quiz',
            step: currentStep,
            button_text: 'Next',
          });
        }
        proceedToNextStep();
      }
    } else if (currentStep === 10) {
      // Phone input step
      if (validateAndProceed()) {
        setIsSubmitting(true);
        if (!hasSubmitted) {
          await submitDataAndRedirect();
        }
        setCurrentStep(currentStep + 1);
      }
    } else if (currentStep === totalSteps) {
      router.push('/');
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setPhoneError(null);
      setCurrentStep(currentStep - 1);
      posthog?.capture('quiz_form_footer_navigation', {
        action_type: 'go_back',
        current_step: currentStep,
        target_step: currentStep - 1,
      });
    }
  };

  const commonTextProps = { fontSize: 'lg' };
  const actionButtonProps = { size: 'lg', colorScheme: 'orange', width: '100%', maxW: 'md', px: 10 };

  const handlePhoneStepProceed = () => {
    if (validateAndProceed()) {
      posthog?.capture('quiz_form_step_proceed', {
        form_type: 'quiz',
        step: 10,
        button_text: 'Confirm Number & See Plans',
      });
      handleMainButtonAction();
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <VStack spacing={4} align="stretch" w="100%">
            <Heading as="h2" size="lg" textAlign="center" mb={2}>
              Take this quiz to find the right Medicare plan for you.
            </Heading>
            <Text {...commonTextProps} textAlign="center" mb={6}>
              There are over 20,000 plans. Fair Square has crunched the numbers to find you the best one.
            </Text>
            <Text {...commonTextProps} textAlign="center" fontWeight="bold">
              Do you currently have Medicare?
            </Text>
            <VStack justify="center" spacing={4} mt={4} align="center" w="100%">
              <Button
                {...actionButtonProps}
                onClick={() => {
                  posthog?.capture('quiz_form_step_answered', {
                    form_type: 'quiz',
                    step: 1,
                    question: 'Do you currently have Medicare?',
                    answer: 'I have Medicare',
                  });
                  setHasMedicare('yes');
                  handleNext();
                }}
              >
                I have Medicare
              </Button>
              <Button
                {...actionButtonProps}
                onClick={() => {
                  posthog?.capture('quiz_form_step_answered', {
                    form_type: 'quiz',
                    step: 1,
                    question: 'Do you currently have Medicare?',
                    answer: "I don't have Medicare yet",
                  });
                  setHasMedicare('no');
                  handleNext();
                }}
                colorScheme="gray"
              >
                I don't have Medicare yet
              </Button>
            </VStack>
          </VStack>
        );
      case 2:
        return (
          <VStack spacing={6} align="stretch" w="100%">
            <Heading as="h2" size="lg" textAlign="center" mb={2}>
              Do you need drug coverage?
            </Heading>
            <VStack justify="center" spacing={4} mt={4} align="center" w="100%">
              <Button
                {...actionButtonProps}
                onClick={() => {
                  posthog?.capture('quiz_form_step_answered', {
                    form_type: 'quiz',
                    step: 2,
                    question: 'Do you need drug coverage?',
                    answer: 'Yes',
                  });
                  setNeedsDrugCoverage('yes');
                  handleNext();
                }}
              >
                Yes
              </Button>
              <Button
                {...actionButtonProps}
                onClick={() => {
                  posthog?.capture('quiz_form_step_answered', {
                    form_type: 'quiz',
                    step: 2,
                    question: 'Do you need drug coverage?',
                    answer: 'No',
                  });
                  setNeedsDrugCoverage('no');
                  handleNext();
                }}
                colorScheme="gray"
              >
                No
              </Button>
            </VStack>
          </VStack>
        );
      case 3:
        return (
          <VStack spacing={6} align="stretch" w="100%">
            <Heading as="h2" size="lg" textAlign="center" mb={2}>
              Do you want hearing benefits?
            </Heading>
            <VStack justify="center" spacing={4} mt={4} align="center" w="100%">
              <Button
                {...actionButtonProps}
                onClick={() => {
                  posthog?.capture('quiz_form_step_answered', {
                    form_type: 'quiz',
                    step: 3,
                    question: 'Do you want hearing benefits?',
                    answer: 'Yes',
                  });
                  setWantsHearingBenefits('yes');
                  handleNext();
                }}
              >
                Yes
              </Button>
              <Button
                {...actionButtonProps}
                onClick={() => {
                  posthog?.capture('quiz_form_step_answered', {
                    form_type: 'quiz',
                    step: 3,
                    question: 'Do you want hearing benefits?',
                    answer: 'No',
                  });
                  setWantsHearingBenefits('no');
                  handleNext();
                }}
                colorScheme="gray"
              >
                No
              </Button>
            </VStack>
          </VStack>
        );
      case 4:
        return (
          <VStack spacing={6} align="stretch" w="100%">
            <Heading as="h2" size="lg" textAlign="center" mb={2}>
              Do you want vision benefits?
            </Heading>
            <VStack justify="center" spacing={4} mt={4} align="center" w="100%">
              <Button
                {...actionButtonProps}
                onClick={() => {
                  posthog?.capture('quiz_form_step_answered', {
                    form_type: 'quiz',
                    step: 4,
                    question: 'Do you want vision benefits?',
                    answer: 'Yes',
                  });
                  setWantsVisionBenefits('yes');
                  handleNext();
                }}
              >
                Yes
              </Button>
              <Button
                {...actionButtonProps}
                onClick={() => {
                  posthog?.capture('quiz_form_step_answered', {
                    form_type: 'quiz',
                    step: 4,
                    question: 'Do you want vision benefits?',
                    answer: 'No',
                  });
                  setWantsVisionBenefits('no');
                  handleNext();
                }}
                colorScheme="gray"
              >
                No
              </Button>
            </VStack>
          </VStack>
        );
      case 5:
        return (
          <VStack spacing={6} align="stretch" w="100%">
            <Heading as="h2" size="lg" textAlign="center" mb={2}>
              Do you want dental benefits?
            </Heading>
            <VStack justify="center" spacing={4} mt={4} align="center" w="100%">
              <Button
                {...actionButtonProps}
                onClick={() => {
                  posthog?.capture('quiz_form_step_answered', {
                    form_type: 'quiz',
                    step: 5,
                    question: 'Do you want dental benefits?',
                    answer: 'Yes',
                  });
                  setWantsDentalBenefits('yes');
                  handleNext();
                }}
              >
                Yes
              </Button>
              <Button
                {...actionButtonProps}
                onClick={() => {
                  posthog?.capture('quiz_form_step_answered', {
                    form_type: 'quiz',
                    step: 5,
                    question: 'Do you want dental benefits?',
                    answer: 'No',
                  });
                  setWantsDentalBenefits('no');
                  handleNext();
                }}
                colorScheme="gray"
              >
                No
              </Button>
            </VStack>
          </VStack>
        );
      case 6:
        return (
          <VStack spacing={6} align="stretch" w="100%">
            <Heading as="h2" size="lg" textAlign="center" mb={2}>
              What is your date of birth?
            </Heading>
            <FormControl isInvalid={false}>
              <FormLabel fontSize="lg" textAlign="center" mb={3}>
                Enter your date of birth:
              </FormLabel>
              <Center>
                <InputGroup size="lg" maxW="sm">
                  <Input
                    type="text"
                    placeholder="MM/DD/YYYY"
                    value={dateOfBirth}
                    onChange={handleDateInputChange}
                    onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter' && dateOfBirth.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                        posthog?.capture('quiz_form_step_proceed', {
                          form_type: 'quiz',
                          step: 6,
                          button_text: 'Next',
                        });
                        handleNext();
                      }
                    }}
                    fontSize="lg"
                    textAlign="center"
                  />
                </InputGroup>
              </Center>
            </FormControl>
            <Center mt={6}>
              <Button
                {...actionButtonProps}
                isDisabled={!dateOfBirth.match(/^\d{2}\/\d{2}\/\d{4}$/)}
                onClick={() => {
                  posthog?.capture('quiz_form_step_proceed', {
                    form_type: 'quiz',
                    step: 6,
                    button_text: 'Next',
                  });
                  handleNext();
                }}
              >
                Next
              </Button>
            </Center>
          </VStack>
        );
      case 7:
        return (
          <VStack spacing={6} align="stretch" w="100%">
            <Heading as="h2" size="lg" textAlign="center" mb={2}>
              What is your ZIP code?
            </Heading>
            <FormControl isInvalid={false}>
              <FormLabel fontSize="lg" textAlign="center" mb={3}>
                Enter your ZIP code:
              </FormLabel>
              <Center>
                <InputGroup size="lg" maxW="sm">
                  <Input
                    type="text"
                    placeholder="ZIP Code"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter' && zipCode.match(/^\d{5}(-\d{4})?$/)) {
                        posthog?.capture('quiz_form_step_proceed', {
                          form_type: 'quiz',
                          step: 7,
                          button_text: 'Next',
                        });
                        handleNext();
                      }
                    }}
                    fontSize="lg"
                    textAlign="center"
                  />
                </InputGroup>
              </Center>
            </FormControl>
            <Center mt={6}>
              <Button
                {...actionButtonProps}
                isDisabled={!zipCode.match(/^\d{5}(-\d{4})?$/)}
                onClick={() => {
                  posthog?.capture('quiz_form_step_proceed', {
                    form_type: 'quiz',
                    step: 7,
                    button_text: 'Next',
                  });
                  handleNext();
                }}
              >
                Next
              </Button>
            </Center>
          </VStack>
        );
      case 8:
        return (
          <VStack spacing={6} align="stretch" w="100%">
            <Heading as="h2" size="lg" textAlign="center" mb={2}>
              What is your first name?
            </Heading>
            <FormControl>
              <FormLabel fontSize="lg" textAlign="center" mb={3}>
                Enter your first name:
              </FormLabel>
              <Center>
                <InputGroup size="lg" maxW="sm">
                  <Input
                    type="text"
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter' && firstName.trim() !== '') {
                        posthog?.capture('quiz_form_step_proceed', {
                          form_type: 'quiz',
                          step: 8,
                          button_text: 'Next',
                        });
                        handleNext();
                      }
                    }}
                    fontSize="lg"
                    textAlign="center"
                  />
                </InputGroup>
              </Center>
            </FormControl>
            <Center mt={6}>
              <Button
                {...actionButtonProps}
                isDisabled={firstName.trim() === ''}
                onClick={() => {
                  posthog?.capture('quiz_form_step_proceed', {
                    form_type: 'quiz',
                    step: 8,
                    button_text: 'Next',
                  });
                  handleNext();
                }}
              >
                Next
              </Button>
            </Center>
          </VStack>
        );
      case 9:
        return (
          <VStack spacing={6} align="stretch" w="100%">
            <Heading as="h2" size="lg" textAlign="center" mb={2}>
              See your recommended plans
            </Heading>
            <Text {...commonTextProps} textAlign="center" mb={4}>
              How do you want to get your list of plans?
            </Text>
            <VStack justify="center" spacing={4} mt={4} align="center" w="100%">
              <Button
                {...actionButtonProps}
                leftIcon={<FaCommentDots />}
                onClick={() => {
                  posthog?.capture('quiz_form_contact_preference_selected', {
                    form_type: 'quiz',
                    step: 9,
                    preference: 'text',
                  });
                  setContactPreference('text');
                  handleNext();
                }}
              >
                Text me my plans
              </Button>
              <Button
                {...actionButtonProps}
                leftIcon={<FaPhoneAlt />}
                onClick={() => {
                  posthog?.capture('quiz_form_contact_preference_selected', {
                    form_type: 'quiz',
                    step: 9,
                    preference: 'call',
                  });
                  setContactPreference('call');
                  handleNext();
                }}
              >
                Call me with my plans
              </Button>
            </VStack>
          </VStack>
        );
      case 10: // Phone number input step
        return (
          <VStack spacing={6} align="stretch" w="100%">
            <Heading as="h2" size="lg" textAlign="center" mb={2}>
              Great news, your plan search is underway...
            </Heading>
            <FormControl isInvalid={!!phoneError}>
              <FormLabel fontSize="lg" textAlign="center" mb={3}>
                Enter your phone number to receive your matches:
              </FormLabel>
              <Center>
                <InputGroup size="lg" maxW="sm">
                  <InputLeftElement pointerEvents="none">
                    <FaPhoneAlt color="gray.300" />
                  </InputLeftElement>
                  <Input
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formattedPhoneNumber}
                    onChange={handlePhoneNumberChange}
                    onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') handlePhoneStepProceed();
                    }}
                    fontSize="lg"
                    pl="3.5rem"
                    width="100%"
                  />
                </InputGroup>
              </Center>
              {phoneError && <FormErrorMessage justifyContent="center">{phoneError}</FormErrorMessage>}
            </FormControl>
            <Text fontSize="2xs" color="gray.800" textAlign="center" mt={3}>
              By submitting my phone number above, I provide my express written consent to receive marketing and
              non-marketing calls and text messages from or on behalf of Virgil Insurance Agency, LLC (DBA Fair Square
              Medicare) at the number I've provided regarding Medicare plans, including Medicare Advantage, Medicare
              Supplement & Prescription Drug Plans, even if my number is on a Do Not Call list and even if an automated
              dialing system or pre-recorded message is used. I understand & agree that my communications with Fair
              Square Medicare are subject to Fair Square Medicare's{' '}
              <ChakraLink href="/privacy-policy" color="blue.500" isExternal>
                Privacy Policy
              </ChakraLink>{' '}
              &{' '}
              <ChakraLink href="/terms-of-service" color="blue.500" isExternal>
                Terms of Service
              </ChakraLink>
              . This is a solicitation of insurance. Contact will be made by a licensed insurance agent. I understand my
              consents to receive text messages and telephone calls are not required for any purchase; I may instead
              reach Fair Square Medicare directly at 888-454-2383; I can revoke my consents at any time by emailing
              donotcall@fairsquaremedicare.com. Message and data rates may apply. Message frequency varies. Text STOP to
              opt-out of messaging.
            </Text>
            <Center mt={6}>
              <Button
                {...actionButtonProps}
                onClick={handlePhoneStepProceed}
                isLoading={isSubmitting}
                isDisabled={isSubmitting || !isValidUSPhoneNumber(rawPhoneNumber)}
              >
                {isSubmitting ? 'Submitting...' : 'Confirm Number & See Plans'}
              </Button>
            </Center>
          </VStack>
        );
      case 11: // Success Page
        return (
          <VStack spacing={4} align="center" w="100%">
            <Heading as="h2" size="lg" textAlign="center" mb={2}>
              Thank You, {firstName}!
            </Heading>
            <Text {...commonTextProps} textAlign="center">
              Your tailored Medicare plan recommendations are on their way.
            </Text>
            <Text {...commonTextProps} textAlign="center" fontWeight={700} mt={4} mb={2}>
              You will receive a {contactPreference === 'text' ? 'text message' : 'call'} from us shortly at{' '}
              {hyphenatePhoneNumber(parseInt(rawPhoneNumber))}.
            </Text>
            <Text {...commonTextProps} textAlign="center" mt={2}>
              If you have any immediate questions, please call us at{' '}
              <ChakraLink href={`tel:${dynamicPhoneNumber}`} color="blue.500" isExternal>
                {hyphenatePhoneNumber(dynamicPhoneNumber)}
              </ChakraLink>
              .
            </Text>
            <Center mt={6}>
              <Button
                {...actionButtonProps}
                onClick={() => router.push(Routes.index)} // Navigate to home or another designated page
              >
                Back to Homepage
              </Button>
            </Center>
          </VStack>
        );
      default:
        return <Text {...commonTextProps}>Step {currentStep}</Text>;
    }
  };

  return (
    <Box>
      <Head>
        <title>Medicare Plan Quiz - Fair Square Medicare</title>
        <meta name="description" content="Take this quiz to find the right Medicare plan for you." />
      </Head>

      <SimplifiedNavbar />

      {/* Main Content Area */}
      <Flex
        direction="column"
        pt={10}
        pb={100}
        px={[4, 6, 8]}
        minH={{
          base: 'calc(100vh - 160px)',
          md: 'calc(100vh - 180px)',
        }}
        alignItems="center"
        justifyContent="flex-start"
      >
        <VStack spacing={8} w="100%" maxW="600px" mt={8}>
          {' '}
          {/* Added margin top for spacing */}
          {renderStepContent()}
        </VStack>
      </Flex>

      {/* Footer with Progress and Navigation */}
      <Box
        as="footer"
        position="fixed"
        bottom="0"
        left="0"
        right="0"
        bg="white"
        borderTop="1px"
        borderColor="gray.200"
        p={4}
        zIndex={10}
        hidden={currentStep === totalSteps}
      >
        <Flex justify="space-between" align="center" maxW="xl" mx="auto">
          <Button
            onClick={handleBack}
            isDisabled={currentStep === 1 || isSubmitting}
            variant="link"
            fontSize="lg"
            color="gray.600"
          >
            {'< BACK'}
          </Button>
          <VStack spacing={1}>
            <Text fontSize="sm" color="orange.500" fontWeight="bold">
              {currentStep}/{totalSteps - 1} {/* Show progress relative to steps before success page*/}
            </Text>
            <Progress
              value={(currentStep / (totalSteps - 1)) * 100}
              size="sm"
              w="150px"
              colorScheme="orange"
              borderRadius="md"
            />
          </VStack>
          <div style={{ width: '80px' }}></div> {/* Placeholder to keep spacing consistent */}
        </Flex>
      </Box>
    </Box>
  );
}
